import type { QueryClient } from '@tanstack/react-query';
import { createKeyedStore } from '@/common/utils/keyedStore';
import { reconcileItemsTreeSet, recordSelfEditedIds } from './itemsTreeSetConsistency';
import { isAnyIdHeld } from './itemsTreeSetHeldArrangement';

type ReconcileOptions = NonNullable<Parameters<typeof reconcileItemsTreeSet>[1]>;

/**
 * The only fields `deriveRetainedIds` needs — deliberately narrower than `ItemTreeSetEntry`, so a
 * caller can pass a table's own already-merged row data instead of the raw tree-set response.
 */
export type SettleTrackableEntry = { id: number; matched: boolean };

export type SettleScope = string;

export type TreeSetSettleState = {
  pendingIds: ReadonlySet<number>;
  settledIds: ReadonlySet<number>;
};

/** How long a row stays flagged `settled` after confirmation, so the highlight is visible. */
export const SETTLED_HIGHLIGHT_MS = 1_500;

const DEFAULT_STATE: TreeSetSettleState = { pendingIds: new Set(), settledIds: new Set() };

const expiryTimersByScope = new Map<SettleScope, Map<number, ReturnType<typeof setTimeout>>>();

// Per-id ownership, so a call whose poll was cancelled or outraced by a newer edit to the same id
// can tell it no longer owns that id's resolution — see `markPending`/`stillOwns`.
const generationByItemId = new Map<number, number>();
let nextGeneration = 0;

const clearExpiryTimer = (scope: SettleScope, id: number) => {
  const timer = expiryTimersByScope.get(scope)?.get(id);
  if (timer === undefined) return;
  clearTimeout(timer);
  expiryTimersByScope.get(scope)?.delete(id);
};

// One scope per mounted table instance — an unbounded key space, so state/timers must drop on
// last unsubscribe, unlike the held-arrangement store's small, stable space.
const store = createKeyedStore<TreeSetSettleState>({
  clearOnUnsubscribe: true,
  onTeardown: (scope) => {
    const timers = expiryTimersByScope.get(scope);
    if (timers) for (const timer of timers.values()) clearTimeout(timer);
    expiryTimersByScope.delete(scope);
  },
});

const scheduleExpiryTimer = (scope: SettleScope, id: number) => {
  clearExpiryTimer(scope, id);
  let timers = expiryTimersByScope.get(scope);
  if (!timers) {
    timers = new Map();
    expiryTimersByScope.set(scope, timers);
  }
  timers.set(
    id,
    setTimeout(() => {
      expiryTimersByScope.get(scope)?.delete(id);
      const previous = store.get(scope) ?? DEFAULT_STATE;
      if (!previous.settledIds.has(id)) return;
      const settledIds = new Set(previous.settledIds);
      settledIds.delete(id);
      store.set(scope, { pendingIds: previous.pendingIds, settledIds });
    }, SETTLED_HIGHLIGHT_MS),
  );
};

export const getSettleState = (scope: SettleScope): TreeSetSettleState =>
  store.get(scope) ?? DEFAULT_STATE;

export const subscribeSettleState = (scope: SettleScope, listener: () => void): (() => void) =>
  store.subscribe(scope, listener);

/** Claims a fresh generation per id, for a later `stillOwns` ownership check. */
const claimGeneration = (itemIds: readonly number[]): ReadonlyMap<number, number> => {
  const claimed = new Map(itemIds.map((id) => [id, ++nextGeneration]));
  for (const [id, generation] of claimed) generationByItemId.set(id, generation);
  return claimed;
};

/** Broadcasts to every mounted scope, in addition to claiming (see `claimGeneration`). */
export const markPending = (itemIds: readonly number[]): ReadonlyMap<number, number> => {
  const claimed = claimGeneration(itemIds);
  if (itemIds.length === 0) return claimed;
  for (const scope of store.activeKeys()) {
    const previous = store.get(scope) ?? DEFAULT_STATE;
    const pendingIds = new Set(previous.pendingIds);
    const settledIds = new Set(previous.settledIds);
    let changed = false;
    for (const id of itemIds) {
      // A re-edit inside the settle window must not let the old expiry clobber the fresh mark.
      clearExpiryTimer(scope, id);
      if (!pendingIds.has(id)) {
        pendingIds.add(id);
        changed = true;
      }
      if (settledIds.delete(id)) changed = true;
    }
    if (changed) store.set(scope, { pendingIds, settledIds });
  }
  return claimed;
};

/**
 * Ids no later `markPending` call has reclaimed — the ones this call still owns and must resolve.
 * Also releases their generation entries, bounding `generationByItemId` to in-flight ids rather
 * than every id ever edited.
 */
const stillOwns = (claimed: ReadonlyMap<number, number>, itemIds: readonly number[]): number[] => {
  const owned = itemIds.filter((id) => generationByItemId.get(id) === claimed.get(id));
  for (const id of owned) generationByItemId.delete(id);
  return owned;
};

/** Drops ids from `pending` without settling them — there was nothing to confirm. */
export const clearPending = (itemIds: readonly number[]): void => {
  if (itemIds.length === 0) return;
  for (const scope of store.activeKeys()) {
    const previous = store.get(scope) ?? DEFAULT_STATE;
    const pendingIds = new Set(previous.pendingIds);
    let changed = false;
    for (const id of itemIds) {
      if (pendingIds.delete(id)) changed = true;
    }
    if (changed) store.set(scope, { pendingIds, settledIds: previous.settledIds });
  }
};

/** Moves ids `pending -> settled` in every mounted scope and arms the settled-highlight expiry. */
export const settlePending = (resolvedIds: readonly number[]): void => {
  if (resolvedIds.length === 0) return;
  for (const scope of store.activeKeys()) {
    const previous = store.get(scope) ?? DEFAULT_STATE;
    const pendingIds = new Set(previous.pendingIds);
    const settledIds = new Set(previous.settledIds);
    let changed = false;
    for (const id of resolvedIds) {
      if (pendingIds.delete(id)) changed = true;
      if (!settledIds.has(id)) {
        settledIds.add(id);
        changed = true;
      }
    }
    if (changed) store.set(scope, { pendingIds, settledIds });
    for (const id of resolvedIds) scheduleExpiryTimer(scope, id);
  }
};

/**
 * Diff mode's shorter attempt budget, applied whenever a caller omits `isReflected` — bounded in
 * the real poll, not a second timer, so `pending` can't be contradicted by a late result.
 * Predicate-based calls keep the longer default via `isReflected`; a reorder polls in diff mode
 * too (no predicate can express "this row's position changed") but overrides back to the longer
 * default explicitly (`useItemPriorityReorder.ts`), since the row still really moves.
 */
export const DIFF_MODE_MAX_ATTEMPTS = 4;

/**
 * Marks `itemIds` pending, then reconciles, recording them as this session's edit (so a later
 * diff — even one tier 2 catches — still classifies as attributable). `settled` fires on
 * convergence or on landing in a held arrangement (the edit went through; only the arrangement
 * awaits Update). Otherwise `pending` clears, but only for ids this call still owns (`stillOwns`).
 * `changedItemIds` defaults to `itemIds` unless overridden.
 */
export const reconcileAndSettle = (
  queryClient: QueryClient,
  itemIds: readonly number[],
  options?: ReconcileOptions,
): Promise<boolean> => {
  const claimed = markPending(itemIds);
  recordSelfEditedIds(queryClient, itemIds);
  const reconciled = reconcileItemsTreeSet(queryClient, {
    changedItemIds: itemIds,
    ...(!options?.isReflected && { maxAttempts: DIFF_MODE_MAX_ATTEMPTS }),
    ...options,
  });

  return reconciled
    .then((converged) => {
      if (converged || isAnyIdHeld(itemIds)) settlePending(stillOwns(claimed, itemIds));
      else clearPending(stillOwns(claimed, itemIds));
      return converged;
    })
    .catch((err) => {
      clearPending(stillOwns(claimed, itemIds));
      throw err;
    });
};

/**
 * Flashes `itemIds` as settled immediately — data's already fetched, nothing left to poll for.
 * Claims ownership like `reconcileAndSettle`'s converged branch, but skips `markPending`'s
 * broadcast so no scope ever observes a pending step first.
 */
export const markAppliedArrangementSettled = (itemIds: readonly number[]): void => {
  if (itemIds.length === 0) return;
  const claimed = claimGeneration(itemIds);
  settlePending(stillOwns(claimed, itemIds));
};

/**
 * Retained once past its pending/settled window while still present with `matched: false`. An
 * id the latest snapshot omits entirely is never retained — there's no row left to apply the
 * class to.
 */
export const deriveRetainedIds = (
  everMarkedIds: ReadonlySet<number>,
  entries: readonly SettleTrackableEntry[],
  pendingIds: ReadonlySet<number>,
  settledIds: ReadonlySet<number>,
): ReadonlySet<number> => {
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  const retained = new Set<number>();
  for (const id of everMarkedIds) {
    if (pendingIds.has(id) || settledIds.has(id)) continue;
    if (byId.get(id)?.matched === false) retained.add(id);
  }
  return retained;
};
